import { Dispatch, HTMLAttributes, SetStateAction, useState } from 'react';

import { FIELD_CONFIG, formatPercent } from 'configs/columns';
import Dinero from 'dinero.js';
import { useDebounce } from 'use-debounce';

import { LoanField } from '__generated__/globalTypes';
import { isNumber, isString } from '../../../../utils/typeUtils';

function isNotNull<T>(action: T | null): action is T {
  return action != null;
}

const List = (props: HTMLAttributes<HTMLUListElement>) => (
  <ul {...props} className="flex-1 list-none p-0" />
);

const Column = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className="flex-grow basis-0" />
);

const SubjectText = (props: HTMLAttributes<HTMLSpanElement>) => (
  <span {...props} className="text-gray-500" />
);

const StyledLabelBody = (props: HTMLAttributes<HTMLDivElement>) => (
  <div {...props} className="flex flex-row gap-4" />
);

const ListItemStyleProps = {
  overrides: {
    Root: {
      style: {
        backgroundColor: '#171717',
        maxHeight: '40px',
      },
    },
  },
};

function useDebouncedState<S>(
  initialState: S | (() => S),
  debounceDelay: number,
  debounceOptions?: {
    maxWait?: number;
    leading?: boolean;
    trailing?: boolean;
    equalityFn?: (left: S, right: S) => boolean;
  },
): [S, S, Dispatch<SetStateAction<S>>] {
  const [value, setValue] = useState<S>(initialState);
  const [dValue] = useDebounce(value, debounceDelay, debounceOptions);

  return [value, dValue, setValue];
}

const percentToBasisPoints = (s: string) => Math.round((+s / 100 - 1) * 10000);
const basisPointsToPercent = (n: number | null) =>
  n || n === 0 ? ((n / 10000 + 1) * 100).toFixed(3) : '';

type FormatCalculatedPriceInput = {
  currentBalanceCents?: number | null;
  priceCents?: number | null;
  rate_bps?: number | null;
};
type FormattedPrice = {
  currentBalance: string;
  price: string;
  rate: string;
};
function formatCalculatedPrice(input: FormatCalculatedPriceInput): FormattedPrice {
  const roundedPrice = input?.priceCents != null ? Math.floor(input.priceCents) : null;
  const config = FIELD_CONFIG[LoanField.current_balance_cents];
  return {
    currentBalance: config.display(input.currentBalanceCents),
    price: config.display(roundedPrice),
    rate: input.rate_bps != null ? formatPercent(input.rate_bps, 3) : '-',
  };
}

function findLastIndex<T>(
  array: Array<T>,
  predicate: (value: T, index: number) => boolean,
): number {
  let l = array.length;
  while (l--) {
    if (predicate(array[l], l)) return l;
  }
  return -1;
}

export {
  basisPointsToPercent,
  Column,
  findLastIndex,
  formatCalculatedPrice,
  isNotNull,
  List,
  ListItemStyleProps,
  percentToBasisPoints,
  StyledLabelBody,
  SubjectText,
  useDebouncedState,
};

export const formatCurrency = (
  value: string | number | Record<string, unknown> | null | undefined,
  withSymbol = true,
): string => {
  if (!isString(value) && !isNumber(value)) {
    return '-';
  }
  const numValue = isString(value) ? Number.parseInt(value) : value;

  return Dinero({
    amount: numValue,
    currency: 'USD',
  }).toFormat(`${withSymbol ? '$' : ''}0,0.00`);
};
